import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  Channel,
  ChannelLogEntry,
  ChannelListing,
  ChannelName,
  ChannelRuntimeReport,
  fetchChannelReport,
  fetchShopifyFullSyncStatus,
  ManapoolInventorySyncStatus,
  fetchManapoolInventorySyncStatus,
  listChannelListings,
  listChannels,
  pollManapoolOrders,
  PollManapoolOrdersResponse,
  refreshManapoolOrderStatuses,
  RefreshManapoolOrderStatusesResponse,
  triggerSync,
  updateChannel,
} from '../api/channels';
import {
  fetchTcgplayerSellerSessionStatus,
  TcgplayerSellerSessionStatus,
} from '../api/pricing';
import { ChannelsSectionNav } from '../components/channels/ChannelsSectionNav';
import { SyncLogsTable } from '../components/channels/SyncLogsTable';
import { SyncStatusBadge } from '../components/channels/SyncStatusBadge';
import { ChannelConfigDrawer } from '../components/channels/ChannelConfigDrawer';
import {
  borderRadius,
  colorExtensions,
  colors,
  shadows,
  spacing,
  typography,
  typographyFlat,
} from '../styles/tokens';
import { buildChannelReportingLogs } from '../lib/channelReportingLogs';
import { formatInstant, formatRelativeTime } from '../utils/datetime';

interface ChannelDetailPageProps {
  channelName: ChannelName;
}

const ChannelDetailPage: React.FC<ChannelDetailPageProps> = ({ channelName }) => {
  const [channel, setChannel] = useState<Channel | null>(null);
  const [listings, setListings] = useState<ChannelListing[]>([]);
  const [isSyncing, setIsSyncing] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [syncResult, setSyncResult] = useState<string | null>(null);
  const [orderPollResult, setOrderPollResult] = useState<string | null>(null);
  const [statusRefreshResult, setStatusRefreshResult] = useState<string | null>(null);
  const [orderPollSummary, setOrderPollSummary] = useState<PollManapoolOrdersResponse | null>(null);
  const [statusRefreshSummary, setStatusRefreshSummary] = useState<RefreshManapoolOrderStatusesResponse | null>(null);
  const [isPollingOrders, setIsPollingOrders] = useState(false);
  const [isRefreshingOrderStatuses, setIsRefreshingOrderStatuses] = useState(false);
  const [showOrderPollAdvanced, setShowOrderPollAdvanced] = useState(false);
  const [orderPollLimit, setOrderPollLimit] = useState<number>(50);
  const [orderPollMaxFetches, setOrderPollMaxFetches] = useState<number>(2);
  const [manualOrderIds, setManualOrderIds] = useState('');
  const [manapoolSyncStatus, setManapoolSyncStatus] = useState<ManapoolInventorySyncStatus | null>(null);
  const [tcgplayerSession, setTcgplayerSession] = useState<TcgplayerSellerSessionStatus | null>(null);
  const [tcgplayerSessionLoading, setTcgplayerSessionLoading] = useState(false);
  const [tcgplayerSessionError, setTcgplayerSessionError] = useState<string | null>(null);
  const [channelReport, setChannelReport] = useState<ChannelRuntimeReport | null>(null);
  const [configOpen, setConfigOpen] = useState(false);
  const requestVersionRef = useRef(0);
  const syncResultTimeoutRef = useRef<number | null>(null);

  const fetchData = useCallback(
    async (silent: boolean = false) => {
      const requestVersion = ++requestVersionRef.current;

      if (!silent) {
        setLoading(true);
      }

      try {
        const [channelsResponse, listingsResponse, reportResponse] = await Promise.all([
          listChannels(),
          listChannelListings(channelName),
          fetchChannelReport(channelName).catch(() => null),
        ]);

        if (requestVersion !== requestVersionRef.current) {
          return;
        }

        const matchedChannel = (channelsResponse.items || []).find((item) => item.name === channelName) || null;
        setChannel(matchedChannel);
        setListings(Array.isArray(listingsResponse.items) ? listingsResponse.items : []);
        setChannelReport(reportResponse);

        if (!matchedChannel) {
          setError('Channel not found');
        } else {
          setError(null);
        }
      } catch (err) {
        if (requestVersion === requestVersionRef.current) {
          setError(err instanceof Error ? err.message : 'Failed to load channel details');
        }
      } finally {
        if (!silent && requestVersion === requestVersionRef.current) {
          setLoading(false);
        }
      }
    },
    [channelName]
  );

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  useEffect(() => {
    const intervalMs = channel?.status === 'syncing' || isSyncing ? 3000 : 30000;
    const intervalId = window.setInterval(() => {
      fetchData(true);
    }, intervalMs);

    return () => window.clearInterval(intervalId);
  }, [channel?.status, fetchData, isSyncing]);

  useEffect(() => {
    if (channelName !== 'manapool') {
      return;
    }

    let cancelled = false;

    const loadStatus = async () => {
      try {
        const status = await fetchManapoolInventorySyncStatus();
        if (!cancelled) {
          setManapoolSyncStatus(status);
        }
      } catch {
        // Keep existing UI behavior if status endpoint is temporarily unavailable.
      }
    };

    loadStatus();
    const intervalId = window.setInterval(loadStatus, 2000);

    return () => {
      cancelled = true;
      window.clearInterval(intervalId);
    };
  }, [channelName]);

  const loadTcgplayerSellerSession = useCallback(async () => {
    if (channelName !== 'tcgplayer') {
      return;
    }
    setTcgplayerSessionLoading(true);
    setTcgplayerSessionError(null);
    try {
      const status = await fetchTcgplayerSellerSessionStatus();
      setTcgplayerSession(status);
    } catch (err) {
      setTcgplayerSession(null);
      setTcgplayerSessionError(
        err instanceof Error ? err.message : 'Could not load seller session status',
      );
    } finally {
      setTcgplayerSessionLoading(false);
    }
  }, [channelName]);

  useEffect(() => {
    if (channelName !== 'tcgplayer') {
      setTcgplayerSession(null);
      setTcgplayerSessionError(null);
      setTcgplayerSessionLoading(false);
      return;
    }

    void loadTcgplayerSellerSession();
    const intervalId = window.setInterval(() => {
      void loadTcgplayerSellerSession();
    }, 30000);

    return () => {
      window.clearInterval(intervalId);
    };
  }, [channelName, loadTcgplayerSellerSession]);

  useEffect(() => {
    return () => {
      if (syncResultTimeoutRef.current !== null) {
        window.clearTimeout(syncResultTimeoutRef.current);
      }
    };
  }, []);

  const counts = useMemo(() => {
    const synced = listings.filter((item) => item.status === 'synced').length;
    const pending = listings.filter((item) => item.status === 'pending').length;
    const failed = listings.filter((item) => item.status === 'failed').length;
    const total = listings.length;
    return { synced, pending, failed, total };
  }, [listings]);

  const progressPercent = useMemo(() => {
    if (counts.total === 0) return 0;
    return Math.round((counts.synced / counts.total) * 100);
  }, [counts.synced, counts.total]);

  const isManapoolChannel = channelName === 'manapool';
  const isManapoolSyncActive = isManapoolChannel && (isSyncing || manapoolSyncStatus?.status === 'running');

  const manapoolProgressPercent = useMemo(() => {
    if (!isManapoolChannel) {
      return progressPercent;
    }
    if (typeof manapoolSyncStatus?.progress_percent === 'number') {
      return Math.max(0, Math.min(100, manapoolSyncStatus.progress_percent));
    }
    return isSyncing ? 10 : 0;
  }, [isManapoolChannel, isSyncing, manapoolSyncStatus?.progress_percent, progressPercent]);

  const manapoolProgressLabel = useMemo(() => {
    if (!isManapoolChannel) {
      return `Sync in progress - ${counts.synced}/${counts.total} listings`;
    }

    if (!manapoolSyncStatus) {
      return 'Inventory reconciliation in progress...';
    }

    if (manapoolSyncStatus.phase === 'fetch' || manapoolSyncStatus.phase === 'fetch_complete') {
      const remoteTotal = manapoolSyncStatus.remote_total_items;
      if (typeof remoteTotal === 'number' && remoteTotal > 0) {
        return `Fetching inventory rows: ${manapoolSyncStatus.fetched_items}/${remoteTotal}`;
      }
      return `Fetching inventory rows: ${manapoolSyncStatus.fetched_items}`;
    }

    if (manapoolSyncStatus.phase === 'reconcile') {
      const total = manapoolSyncStatus.total_variants || 0;
      const processed = manapoolSyncStatus.processed_variants || 0;
      return `Reconciling variants: ${processed}/${total} (${manapoolSyncStatus.copies_created} created, ${manapoolSyncStatus.copies_deleted} deleted)`;
    }

    return manapoolSyncStatus.message || 'Inventory reconciliation in progress...';
  }, [counts.synced, counts.total, isManapoolChannel, manapoolSyncStatus]);

  const reportingTotals = channelReport?.listing_totals ?? counts;
  const reportingActivity = channelReport?.recent_activity;
  const observedThroughputPerMin = channelReport?.throughput_per_min_last_5m ?? 0;
  const estimatedMinutesRemaining =
    reportingTotals.pending > 0 && observedThroughputPerMin > 0
      ? reportingTotals.pending / observedThroughputPerMin
      : null;
  const remoteShopifySyncBusy =
    channelName === 'shopify' && Boolean(channelReport?.sync_in_progress) && !isSyncing;
  const isChannelSyncActive =
    isSyncing || channel?.status === 'syncing' || Boolean(channelReport?.sync_in_progress);

  const reportingLogs = useMemo<ChannelLogEntry[]>(
    () =>
      buildChannelReportingLogs({
        channelId: channel?.id,
        channelName,
        lastError: channel?.last_error,
        lastSyncAt: channel?.last_sync_at,
        report: channelReport,
      }),
    [channel?.id, channel?.last_error, channel?.last_sync_at, channelName, channelReport],
  );

  const handleSync = useCallback(async (dryRun = false) => {
    setError(null);
    setSyncResult(null);

    const isShopify = channelName === 'shopify';
    const isManapool = channelName === 'manapool';

    if (isShopify) {
      const shouldProceed = window.confirm(
        'Sync all in-stock inventory to Shopify now?\n\n'
        + 'This starts a server-side full catalog sync. You can leave this page; it keeps running on the server.',
      );
      if (!shouldProceed) {
        return;
      }

      setIsSyncing(true);
      try {
        const result = await triggerSync(channelName, {
          sync_all_in_stock: true,
          dry_run: false,
        });
        if (!result.accepted) {
          setSyncResult(
            `Shopify sync complete: ${result.success_count}/${result.total} succeeded, ${result.failed_count} failed.`,
          );
          await fetchData(true);
          return;
        }

        setSyncResult(result.message || 'Shopify full catalog sync started on the server…');
        // Poll server status so the UI can show progress without owning the job.
        let stillRunning = true;
        while (stillRunning) {
          await new Promise((resolve) => window.setTimeout(resolve, 3000));
          const status = await fetchShopifyFullSyncStatus();
          setSyncResult(
            status.message
            || `Shopify sync ${status.status}`
            + (status.batches_completed ? ` · ${status.batches_completed} batches` : '')
            + (status.total ? ` · ${status.success_count} ok / ${status.failed_count} failed` : ''),
          );
          await fetchData(true);
          stillRunning = ['accepted', 'running'].includes(status.status);
          if (!stillRunning && status.status === 'failed') {
            setError(status.last_error || status.message || 'Shopify sync failed');
          }
        }
        if (syncResultTimeoutRef.current !== null) {
          window.clearTimeout(syncResultTimeoutRef.current);
        }
        syncResultTimeoutRef.current = window.setTimeout(() => {
          setSyncResult(null);
          syncResultTimeoutRef.current = null;
        }, 15000);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Sync failed');
      } finally {
        setIsSyncing(false);
      }
      return;
    }

    if (isManapool && !dryRun) {
      const shouldProceed = window.confirm(
        'APPLY inbound Manapool reconciliation?\n\n'
        + 'This compares Manapool listings to SyncBase and may CREATE or DELETE copies.\n'
        + 'Prefer Channel Parity remediation to push SyncBase quantities outbound.\n'
        + 'Use Detect first — do not use Apply to “fix” Manapool post-order lag.\n\n'
        + 'Continue with APPLY?',
      );
      if (!shouldProceed) {
        return;
      }
    }

    setIsSyncing(true);

    try {
      const payload = isManapool
        ? { policy_mode: dryRun ? 'detect' as const : 'apply' as const }
        : undefined;
      const result = await triggerSync(channelName, payload);
      if (isManapool) {
        try {
          const status = await fetchManapoolInventorySyncStatus();
          setManapoolSyncStatus(status);
        } catch {
          // Non-fatal if status endpoint races with completion.
        }
      }
      if (isManapool && dryRun) {
        setSyncResult(
          `Detect complete (${result.policy_mode ?? 'detect'}): ${result.message || `${result.success_count} variants compared`}. `
          + 'No SyncBase copies were created or deleted. Use Channel Parity for outbound qty fixes.',
        );
      } else if (result.accepted) {
        setSyncResult(result.message || `${channelName} sync accepted. Refreshing channel status...`);
      } else {
        setSyncResult(
          `${channelName} sync complete: ${result.success_count}/${result.total} succeeded, ${result.failed_count} failed.`
        );
      }
      if (syncResultTimeoutRef.current !== null) {
        window.clearTimeout(syncResultTimeoutRef.current);
      }
      syncResultTimeoutRef.current = window.setTimeout(() => {
        setSyncResult(null);
        syncResultTimeoutRef.current = null;
      }, dryRun ? 12000 : 8000);
      await fetchData(true);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Sync failed');
    } finally {
      setIsSyncing(false);
    }
  }, [channelName, fetchData]);

  const handlePollManapoolOrders = useCallback(async () => {
    setError(null);
    setOrderPollResult(null);
    setIsPollingOrders(true);

    try {
      const summary = showOrderPollAdvanced
        ? await pollManapoolOrders({
            limit: orderPollLimit,
            max_fetches: orderPollMaxFetches,
          })
        : await pollManapoolOrders();
      setOrderPollSummary(summary);
      setOrderPollResult(
        `Order poll complete: ${summary.orders_queued} queued, ${summary.orders_skipped} skipped, ${summary.orders_fetched} fetched across ${summary.batches_fetched} batches.`
      );
      await fetchData(true);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Order poll failed');
    } finally {
      setIsPollingOrders(false);
    }
  }, [fetchData, orderPollLimit, orderPollMaxFetches, showOrderPollAdvanced]);

  const handleRefreshManapoolStatuses = useCallback(async () => {
    setError(null);
    setStatusRefreshResult(null);
    setIsRefreshingOrderStatuses(true);

    try {
      const summary = await refreshManapoolOrderStatuses({
        limit: showOrderPollAdvanced ? orderPollLimit : 100,
      });
      setStatusRefreshSummary(summary);
      const failures = (summary.results || []).filter((row) => row.status !== 'success');
      if (failures.length > 0) {
        const failureText = failures
          .slice(0, 5)
          .map((row) => `${row.order_id || row.external_id || '?'}: ${row.error || row.status}`)
          .join('; ');
        setError(
          `Status refresh failed for ${failures.length}/${summary.candidate_count}: ${failureText}`
          + (failures.length > 5 ? ' …' : ''),
        );
      }
      setStatusRefreshResult(
        `Status refresh (${summary.mode}): ${summary.success_count ?? 0} ok, ${summary.error_count ?? 0} failed`
        + ` · ${summary.candidate_count} pending candidates`,
      );
      const statusBits = (summary.results || [])
        .filter((row) => row.fulfillment_status || row.manapool_status || row.seller_list_status)
        .slice(0, 8)
        .map((row) => {
          const id = row.external_id || row.order_id || '?';
          return `${id}: mp=${row.manapool_status || '—'} list=${row.seller_list_status || '—'} → ${row.fulfillment_status || '—'}/${row.commercial_status || '—'}`;
        });
      if (statusBits.length) {
        setStatusRefreshResult((prev) => `${prev}\n${statusBits.join('\n')}`);
      }
      await fetchData(true);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Status refresh failed');
    } finally {
      setIsRefreshingOrderStatuses(false);
    }
  }, [fetchData, orderPollLimit, showOrderPollAdvanced]);

  const parseManualOrderIds = useCallback((raw: string): string[] => {
    const seen = new Set<string>();
    const ids: string[] = [];
    for (const part of raw.split(/[\s,;]+/)) {
      const orderId = part.trim();
      if (!orderId || seen.has(orderId)) {
        continue;
      }
      seen.add(orderId);
      ids.push(orderId);
    }
    return ids;
  }, []);

  const handleImportManapoolOrderIds = useCallback(async () => {
    const orderIds = parseManualOrderIds(manualOrderIds);
    if (orderIds.length === 0) {
      setError('Enter at least one Manapool order ID to import.');
      return;
    }

    setError(null);
    setStatusRefreshResult(null);
    setIsRefreshingOrderStatuses(true);

    try {
      const summary = await refreshManapoolOrderStatuses({ order_ids: orderIds });
      setStatusRefreshSummary(summary);
      const failures = (summary.results || []).filter((row) => row.status !== 'success');
      const successBits = (summary.results || [])
        .filter((row) => row.status === 'success')
        .map((row) => row.external_id || row.order_id)
        .filter(Boolean);
      if (failures.length > 0) {
        const failureText = failures
          .map((row) => `${row.order_id || row.external_id || '?'}: ${row.error || row.status}`)
          .join('; ');
        setError(`Order import failed for ${failures.length}/${orderIds.length}: ${failureText}`);
      }
      setStatusRefreshResult(
        `Import by ID (${summary.mode}): ${summary.success_count ?? 0} ok, ${summary.error_count ?? 0} failed`
        + (successBits.length ? ` · ${successBits.join(', ')}` : ''),
      );
      const statusBits = (summary.results || [])
        .filter((row) => row.fulfillment_status || row.manapool_status || row.seller_list_status)
        .slice(0, 8)
        .map((row) => {
          const id = row.external_id || row.order_id || '?';
          return `${id}: mp=${row.manapool_status || '—'} list=${row.seller_list_status || '—'} → ${row.fulfillment_status || '—'}/${row.commercial_status || '—'}`;
        });
      if (statusBits.length) {
        setStatusRefreshResult((prev) => `${prev}\n${statusBits.join('\n')}`);
      }
      await fetchData(true);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Order import by ID failed');
    } finally {
      setIsRefreshingOrderStatuses(false);
    }
  }, [fetchData, manualOrderIds, parseManualOrderIds]);

  const formatDate = (timestamp: string | null | undefined) => {
    return formatInstant(timestamp, 'Never');
  };

  return (
    <>
      <div style={styles.container}>
        <div style={styles.header}>
          <h1 style={styles.title}>Channels</h1>
          <p style={styles.subtitle}>Detailed sync health and operational controls for this channel.</p>
          <ChannelsSectionNav />
        </div>

        {channelName === 'manapool' && (
          <div style={styles.safetyBanner}>
            <div style={styles.safetyBannerHeader}>
              <strong>Manapool inbound tools</strong>
            </div>
            <p style={styles.metaText}>
              Detect is read-only. Apply mutates SyncBase copies and is expert-only.
              Prefer <Link to="/reports/channel-parity" style={styles.inlineLink}>Channel Parity</Link> for
              outbound quantity remediation.
            </p>
          </div>
        )}

        {channelName === 'tcgplayer' && (
          <div style={styles.safetyBanner}>
            <div style={styles.safetyBannerHeader}>
              <strong>TCGPlayer outbound fan-out</strong>
            </div>
            <p style={styles.metaText}>
              SyncBase stays source of truth. When outbound inventory sync is on, stock and price
              changes fan out via the outbox to the seller portal (Pokémon and Lorcana singles).
              There is no catalog Sync Now and no List / Update / Remove desk — keep the Chrome
              extension seller session fresh and use Configure for the outbound flag. Prefer
              Test canary; do not casually enable outbound on Prod.
            </p>
          </div>
        )}

        {loading && (
          <div style={styles.loadingText}>Loading channel health...</div>
        )}

        {error && (
          <div style={styles.errorBox}>
            {error}
            <div style={{ marginTop: spacing.sm }}>
              <button type="button" style={styles.secondaryButton} onClick={() => void fetchData(false)}>
                Retry
              </button>
            </div>
          </div>
        )}

        {syncResult && (
          <div style={styles.successBox}>
            {syncResult}
          </div>
        )}

        {channel && (
          <>
            <div style={styles.card}>
              <div style={styles.cardHeader}>
                <div>
                  <h2 style={styles.cardTitle}>{channel.display_name}</h2>
                  <div style={styles.metaText}>Last sync: {formatDate(channel.last_sync_at)} ({formatRelativeTime(channel.last_sync_at, { fallback: 'No recent sync', maxDays: Infinity })})</div>
                  <div style={styles.metaText}>Sync enabled: {channel.sync_enabled ? 'Yes' : 'No'}</div>
                  <div style={styles.metaText}>Inbound inventory sync: {channel.inbound_inventory_sync_enabled ? 'On' : 'Off'}</div>
                  <div style={styles.metaText}>Outbound inventory sync: {channel.outbound_inventory_sync_enabled ? 'On' : 'Off'}</div>
                  <div style={styles.metaText}>Orders webhook: {channel.orders_webhook_enabled ? 'On' : 'Off'}</div>
                </div>
                <SyncStatusBadge status={channel.status || 'inactive'} />
              </div>

              <div style={styles.buttonRow}>
                {channelName === 'shopify' && (
                  <button
                    type="button"
                    onClick={() => void handleSync(false)}
                    disabled={isChannelSyncActive}
                    style={{
                      ...styles.primaryButton,
                      ...(isChannelSyncActive ? styles.primaryButtonDisabled : {}),
                    }}
                    title={
                      remoteShopifySyncBusy
                        ? 'A Shopify sync lock is still held — wait or restart app if stuck'
                        : 'Push all in-stock inventory to Shopify'
                    }
                  >
                    {isSyncing
                      ? 'Syncing…'
                      : remoteShopifySyncBusy
                        ? 'Sync Busy…'
                        : 'Sync Now'}
                  </button>
                )}
                {channelName === 'manapool' && (
                  <button
                    type="button"
                    onClick={() => void handleSync(true)}
                    disabled={isChannelSyncActive}
                    style={{
                      ...styles.primaryButton,
                      ...(isChannelSyncActive ? styles.primaryButtonDisabled : {}),
                    }}
                  >
                    {isChannelSyncActive ? 'Detect Running...' : 'Detect Drift'}
                  </button>
                )}
                {channelName === 'manapool' && (
                  <button
                    type="button"
                    onClick={() => void handleSync(false)}
                    disabled={isChannelSyncActive}
                    style={{
                      ...styles.secondaryButton,
                      ...(isChannelSyncActive ? styles.primaryButtonDisabled : {}),
                    }}
                    title="Expert-only: mutates SyncBase copies to match Manapool"
                  >
                    {isSyncing ? 'Apply Running...' : 'Apply Inbound (expert)'}
                  </button>
                )}
                <button type="button" onClick={() => setConfigOpen(true)} style={styles.secondaryButton}>
                  Configure
                </button>
                <Link to="/reports/channel-parity" style={styles.linkButton}>
                  Open Channel Parity
                </Link>
              </div>
              {channelName === 'shopify' && (
                <p style={styles.metaText}>
                  Sync Now starts a server-side full in-stock catalog push to Shopify.
                  Leaving the page is safe — the job keeps running on the app.
                  Day-to-day changes still fan out via the outbox.
                </p>
              )}
              {channelName === 'manapool' && (
                <p style={styles.metaText}>
                  Detect compares Manapool to SyncBase without writing copies. Apply mutates SyncBase and
                  should not be used to chase post-order Manapool lag — use Channel Parity for confidence
                  and outbound remediation.
                  {manapoolSyncStatus?.shared === false && (
                    <> Status backend is process-local; progress may be incomplete under multi-worker.</>
                  )}
                </p>
              )}
              {channelName === 'tcgplayer' && (
                <p style={styles.metaText}>
                  Day-to-day listing writes happen through outbox fan-out when outbound is on.
                  Configure toggles sync / outbound / webhook flags for this channel.
                </p>
              )}
            </div>

            {channelName === 'tcgplayer' && (
              <div style={styles.card}>
                <div style={styles.cardHeader}>
                  <div>
                    <h2 style={styles.cardTitle}>Seller session</h2>
                    <div style={styles.metaText}>
                      Vault-backed cookie jar from the Chrome extension. Fan-out will not write while
                      the session is missing or stale.
                    </div>
                  </div>
                  <button
                    type="button"
                    onClick={() => void loadTcgplayerSellerSession()}
                    disabled={tcgplayerSessionLoading}
                    style={{
                      ...styles.secondaryButton,
                      ...(tcgplayerSessionLoading ? styles.primaryButtonDisabled : {}),
                    }}
                  >
                    {tcgplayerSessionLoading ? 'Refreshing…' : 'Refresh session status'}
                  </button>
                </div>
                {tcgplayerSessionError && (
                  <div style={styles.lastErrorBox}>{tcgplayerSessionError}</div>
                )}
                {!tcgplayerSessionError && tcgplayerSessionLoading && !tcgplayerSession && (
                  <div style={styles.metaText}>Checking seller session…</div>
                )}
                {tcgplayerSession && (
                  <div style={styles.orderSummaryGrid}>
                    <div style={styles.statCard}>
                      <div style={styles.statLabel}>Status</div>
                      <div style={styles.statValue}>{tcgplayerSession.status || 'unknown'}</div>
                    </div>
                    <div style={styles.statCard}>
                      <div style={styles.statLabel}>Freshness</div>
                      <div style={styles.statValue}>
                        {tcgplayerSession.stale ? 'Stale' : 'Fresh'}
                      </div>
                    </div>
                    <div style={styles.statCard}>
                      <div style={styles.statLabel}>Updated</div>
                      <div style={styles.statValue}>
                        {formatRelativeTime(tcgplayerSession.updated_at, {
                          fallback: 'Never',
                          maxDays: Number.POSITIVE_INFINITY,
                        })}
                      </div>
                    </div>
                    <div style={styles.statCard}>
                      <div style={styles.statLabel}>Auth ticket</div>
                      <div style={styles.statValue}>
                        {tcgplayerSession.has_auth_ticket ? 'Present' : 'Missing'}
                      </div>
                    </div>
                  </div>
                )}
                {tcgplayerSession?.stale_reason && (
                  <div style={styles.lastErrorBox}>{tcgplayerSession.stale_reason}</div>
                )}
                {tcgplayerSession?.updated_at && (
                  <div style={styles.metaText}>
                    Absolute: {formatInstant(tcgplayerSession.updated_at)}
                  </div>
                )}
              </div>
            )}

            {(channel.status === 'syncing' || isSyncing || isManapoolSyncActive || channelReport?.sync_in_progress) && (
              <div style={styles.progressSection}>
                <div style={styles.progressLabel}>
                  {isManapoolChannel ? manapoolProgressLabel : `Sync in progress - ${counts.synced}/${counts.total} listings`}
                </div>
                <div style={styles.progressTrack}>
                  <div
                    style={{
                      ...styles.progressFill,
                      width: isManapoolChannel ? `${manapoolProgressPercent}%` : `${progressPercent}%`,
                    }}
                  />
                </div>
              </div>
            )}

            <div style={styles.reportingPanel}>
              <h3 style={styles.logsTitle}>Live Reporting</h3>
              <div style={styles.reportingMetaRow}>
                <div style={styles.metaText}>Runtime status: {channelReport?.inferred_status ?? 'idle'}</div>
                <div style={styles.metaText}>
                  Last listing activity: {formatDate(channelReport?.last_listing_update_at)} ({formatRelativeTime(channelReport?.last_listing_update_at, { fallback: 'No recent sync', maxDays: Infinity })})
                </div>
                <div style={styles.metaText}>Observed throughput (5m): {observedThroughputPerMin.toFixed(2)} listings/min</div>
                <div style={styles.metaText}>
                  ETA to clear pending:{' '}
                  {estimatedMinutesRemaining === null
                    ? 'N/A'
                    : estimatedMinutesRemaining < 1
                      ? '< 1m'
                      : `${Math.round(estimatedMinutesRemaining)}m`}
                </div>
              </div>
              <div style={styles.orderSummaryGrid}>
                <div style={styles.statCard}>
                  <div style={styles.statLabel}>Updated (1m)</div>
                  <div style={styles.statValue}>{reportingActivity?.updated_last_1m ?? 0}</div>
                </div>
                <div style={styles.statCard}>
                  <div style={styles.statLabel}>Updated (5m)</div>
                  <div style={styles.statValue}>{reportingActivity?.updated_last_5m ?? 0}</div>
                </div>
                <div style={styles.statCard}>
                  <div style={styles.statLabel}>Updated (15m)</div>
                  <div style={styles.statValue}>{reportingActivity?.updated_last_15m ?? 0}</div>
                </div>
              </div>
            </div>

            {channel.last_error && (
              <div style={styles.lastErrorBox}>Last error: {channel.last_error}</div>
            )}

            <div style={styles.statsGrid}>
              <div style={styles.statCard}>
                <div style={styles.statLabel}>Synced Listings</div>
                <div style={styles.statValue}>{reportingTotals.synced}</div>
              </div>
              <div style={styles.statCard}>
                <div style={styles.statLabel}>Pending Listings</div>
                <div style={styles.statValue}>{reportingTotals.pending}</div>
              </div>
              <div style={styles.statCard}>
                <div style={styles.statLabel}>Failed Listings</div>
                <div style={styles.statValue}>{reportingTotals.failed}</div>
              </div>
              <div style={styles.statCard}>
                <div style={styles.statLabel}>Total Listings</div>
                <div style={styles.statValue}>{reportingTotals.total}</div>
              </div>
            </div>

            <div style={styles.logsSection}>
              {isManapoolChannel && (
                <div style={styles.orderPanel}>
                  <h3 style={styles.logsTitle}>Order Ingestion</h3>
                  <div style={styles.metaText}>Manual backfill/poll from Manapool orders API. Default uses full pull bounds.</div>
                  <div style={styles.orderControlRow}>
                    <button
                      type="button"
                      onClick={handlePollManapoolOrders}
                      disabled={isPollingOrders}
                      style={{
                        ...styles.secondaryButton,
                        ...(isPollingOrders ? styles.primaryButtonDisabled : {}),
                      }}
                    >
                      {isPollingOrders ? 'Polling Orders...' : 'Poll Manapool Orders'}
                    </button>
                    <button
                      type="button"
                      onClick={handleRefreshManapoolStatuses}
                      disabled={isRefreshingOrderStatuses}
                      style={{
                        ...styles.secondaryButton,
                        ...(isRefreshingOrderStatuses ? styles.primaryButtonDisabled : {}),
                      }}
                    >
                      {isRefreshingOrderStatuses ? 'Refreshing Statuses...' : 'Refresh Pending Statuses'}
                    </button>
                    <button
                      type="button"
                      onClick={() => setShowOrderPollAdvanced((prev) => !prev)}
                      style={styles.tertiaryButton}
                    >
                      {showOrderPollAdvanced ? 'Hide Advanced' : 'Show Advanced'}
                    </button>
                  </div>
                    <div style={styles.orderControlRow}>
                    <label style={{ ...styles.orderControlLabel, flex: 1, minWidth: 240 }}>
                      Import missed order by ID
                      <input
                        type="text"
                        value={manualOrderIds}
                        onChange={(e) => setManualOrderIds(e.target.value)}
                        placeholder="Manapool order # (e.g. 1234-5678), comma-separated"
                        style={{ ...styles.orderControlInput, minWidth: 280, width: '100%' }}
                      />
                    </label>
                    <button
                      type="button"
                      onClick={() => void handleImportManapoolOrderIds()}
                      disabled={isRefreshingOrderStatuses || parseManualOrderIds(manualOrderIds).length === 0}
                      style={{
                        ...styles.secondaryButton,
                        alignSelf: 'flex-end',
                        ...(isRefreshingOrderStatuses || parseManualOrderIds(manualOrderIds).length === 0
                          ? styles.primaryButtonDisabled
                          : {}),
                      }}
                    >
                      {isRefreshingOrderStatuses ? 'Importing...' : 'Import Order ID'}
                    </button>
                  </div>
                  <div style={styles.metaText}>
                    Accepts the order number from the Manapool seller UI (e.g. 1234-5678) or a UUID.
                    SyncBase resolves the label to Manapool&apos;s UUID, then ingests (same path as webhook).
                  </div>
                  {showOrderPollAdvanced && (
                    <div style={styles.orderControlRow}>
                      <label style={styles.orderControlLabel}>
                        Limit
                        <input
                          type="number"
                          min={1}
                          max={500}
                          value={orderPollLimit}
                          onChange={(e) => setOrderPollLimit(Math.max(1, Math.min(500, Number(e.target.value) || 1)))}
                          style={styles.orderControlInput}
                        />
                      </label>
                      <label style={styles.orderControlLabel}>
                        Max Fetches
                        <input
                          type="number"
                          min={1}
                          max={50}
                          value={orderPollMaxFetches}
                          onChange={(e) => setOrderPollMaxFetches(Math.max(1, Math.min(50, Number(e.target.value) || 1)))}
                          style={styles.orderControlInput}
                        />
                      </label>
                    </div>
                  )}
                  {orderPollResult && <div style={styles.orderResultText}>{orderPollResult}</div>}
                  {statusRefreshResult && <div style={styles.orderResultText}>{statusRefreshResult}</div>}
                  {orderPollSummary && (
                    <div style={styles.orderSummaryGrid}>
                      <div style={styles.statCard}>
                        <div style={styles.statLabel}>Orders Fetched</div>
                        <div style={styles.statValue}>{orderPollSummary.orders_fetched}</div>
                      </div>
                      <div style={styles.statCard}>
                        <div style={styles.statLabel}>Orders Queued</div>
                        <div style={styles.statValue}>{orderPollSummary.orders_queued}</div>
                      </div>
                      <div style={styles.statCard}>
                        <div style={styles.statLabel}>Orders Skipped</div>
                        <div style={styles.statValue}>{orderPollSummary.orders_skipped}</div>
                      </div>
                      <div style={styles.statCard}>
                        <div style={styles.statLabel}>Batches Fetched</div>
                        <div style={styles.statValue}>{orderPollSummary.batches_fetched}</div>
                      </div>
                    </div>
                  )}
                  {statusRefreshSummary && (
                    <div style={styles.orderSummaryGrid}>
                      <div style={styles.statCard}>
                        <div style={styles.statLabel}>Status Candidates</div>
                        <div style={styles.statValue}>{statusRefreshSummary.candidate_count}</div>
                      </div>
                      <div style={styles.statCard}>
                        <div style={styles.statLabel}>Status OK</div>
                        <div style={styles.statValue}>{statusRefreshSummary.success_count ?? 0}</div>
                      </div>
                      <div style={styles.statCard}>
                        <div style={styles.statLabel}>Status Failed</div>
                        <div style={styles.statValue}>{statusRefreshSummary.error_count ?? 0}</div>
                      </div>
                    </div>
                  )}
                </div>
              )}

              <h3 style={styles.logsTitle}>Recent Logs</h3>
              <SyncLogsTable logs={reportingLogs} emptyMessage="No recent sync diagnostics yet" />
            </div>
          </>
        )}
      </div>

      <ChannelConfigDrawer
        channel={channel}
        isOpen={configOpen}
        onClose={() => setConfigOpen(false)}
        onSave={async (updated) => {
            try {
              await updateChannel(channelName, updated);
              await fetchData(true);
            } catch (err) {
              setError(err instanceof Error ? err.message : 'Failed to save channel config');
            }
          }}
      />
    </>
  );
};

const styles = {
  container: {
    padding: spacing.lg,
    display: 'flex',
    flexDirection: 'column' as const,
    gap: spacing.lg,
  },
  header: {
    display: 'flex',
    flexDirection: 'column' as const,
    gap: spacing.xs,
  },
  title: {
    margin: 0,
    fontSize: typography.h1.fontSize,
    fontWeight: typography.h1.fontWeight,
    color: colors.text,
  },
  subtitle: {
    margin: 0,
    color: colors.textSecondary,
    fontSize: typography.body.fontSize,
  },
  safetyBanner: {
    padding: spacing.md,
    borderRadius: borderRadius.md,
    border: `1px solid ${colors.warning}`,
    backgroundColor: `${colors.warning}18`,
    display: 'flex',
    flexDirection: 'column' as const,
    gap: spacing.sm,
  },
  safetyBannerHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    gap: spacing.md,
    alignItems: 'center',
    flexWrap: 'wrap' as const,
  },
  safeModeToggle: {
    display: 'flex',
    alignItems: 'center',
    gap: spacing.xs,
    color: colors.text,
    fontSize: typography.small.fontSize,
    cursor: 'pointer',
  },
  inlineLink: {
    color: colors.primary,
    fontWeight: typographyFlat.fontWeight.semibold,
  },
  linkButton: {
    border: `1px solid ${colors.border}`,
    borderRadius: borderRadius.md,
    padding: `${spacing.sm} ${spacing.md}`,
    textDecoration: 'none',
    color: colors.text,
    backgroundColor: colors.surface,
    fontSize: typography.body.fontSize,
    fontWeight: typographyFlat.fontWeight.semibold,
    display: 'inline-flex',
    alignItems: 'center',
  },
  limitLabel: {
    display: 'flex',
    flexDirection: 'column' as const,
    gap: spacing.xs,
    color: colors.textSecondary,
    fontSize: typography.small.fontSize,
  },
  limitInput: {
    width: '100px',
    border: `1px solid ${colors.border}`,
    borderRadius: borderRadius.sm,
    padding: `${spacing.xs} ${spacing.sm}`,
    backgroundColor: colors.white,
    color: colors.text,
  },
  loadingText: {
    color: colors.textSecondary,
    fontSize: typography.body.fontSize,
  },
  errorBox: {
    padding: spacing.md,
    backgroundColor: `${colors.error}10`,
    borderRadius: borderRadius.md,
    color: colors.error,
  },
  successBox: {
    padding: spacing.md,
    backgroundColor: `${colors.success}10`,
    borderRadius: borderRadius.md,
    color: colors.success,
  },
  card: {
    border: `1px solid ${colors.border}`,
    borderRadius: borderRadius.lg,
    backgroundColor: colors.surface,
    boxShadow: shadows.sm,
    padding: spacing.lg,
    display: 'flex',
    flexDirection: 'column' as const,
    gap: spacing.md,
  },
  cardHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    gap: spacing.md,
  },
  cardTitle: {
    margin: 0,
    fontSize: typography.h2.fontSize,
    fontWeight: typography.h2.fontWeight,
    color: colors.text,
  },
  metaText: {
    marginTop: spacing.xs,
    color: colors.textSecondary,
    fontSize: typography.body.fontSize,
  },
  buttonRow: {
    display: 'flex',
    gap: spacing.sm,
    flexWrap: 'wrap' as const,
    alignItems: 'flex-end',
  },
  primaryButton: {
    padding: `${spacing.sm} ${spacing.md}`,
    backgroundColor: colors.primary,
    color: colors.white,
    border: 'none',
    borderRadius: borderRadius.md,
    fontSize: typography.body.fontSize,
    fontWeight: typography.body.fontWeight,
    cursor: 'pointer',
  },
  primaryButtonDisabled: {
    backgroundColor: colors.border,
    cursor: 'not-allowed',
  },
  secondaryButton: {
    padding: `${spacing.sm} ${spacing.md}`,
    backgroundColor: 'transparent',
    color: colors.primary,
    border: `1px solid ${colors.primary}`,
    borderRadius: borderRadius.md,
    fontSize: typography.body.fontSize,
    fontWeight: typography.body.fontWeight,
    cursor: 'pointer',
  },
  tertiaryButton: {
    padding: `${spacing.sm} ${spacing.md}`,
    backgroundColor: 'transparent',
    color: colors.textSecondary,
    border: `1px dashed ${colors.border}`,
    borderRadius: borderRadius.md,
    fontSize: typography.body.fontSize,
    fontWeight: typography.body.fontWeight,
    cursor: 'pointer',
  },
  progressSection: {
    padding: spacing.md,
    borderRadius: borderRadius.md,
    backgroundColor: colors.background,
    border: `1px solid ${colors.border}`,
    display: 'flex',
    flexDirection: 'column' as const,
    gap: spacing.sm,
  },
  progressLabel: {
    color: colors.text,
    fontSize: typographyFlat.fontSize.sm,
    fontWeight: typographyFlat.fontWeight.semibold,
  },
  progressTrack: {
    height: '8px',
    borderRadius: borderRadius.full,
    backgroundColor: colors.gray200,
    overflow: 'hidden',
  },
  progressFill: {
    height: '100%',
    backgroundColor: colors.primary,
    transition: 'width 0.5s ease',
  },
  lastErrorBox: {
    padding: spacing.md,
    borderRadius: borderRadius.md,
    backgroundColor: `${colors.error}10`,
    color: colorExtensions.errorDark,
    border: `1px solid ${colors.error}`,
  },
  statsGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
    gap: spacing.md,
  },
  statCard: {
    border: `1px solid ${colors.border}`,
    borderRadius: borderRadius.md,
    backgroundColor: colors.surface,
    padding: spacing.md,
  },
  statLabel: {
    fontSize: typographyFlat.fontSize.sm,
    color: colors.textSecondary,
  },
  statValue: {
    marginTop: spacing.xs,
    fontSize: typography.h3.fontSize,
    fontWeight: typography.h3.fontWeight,
    color: colors.text,
  },
  logsSection: {
    display: 'flex',
    flexDirection: 'column' as const,
    gap: spacing.sm,
  },
  reportingPanel: {
    border: `1px solid ${colors.border}`,
    borderRadius: borderRadius.md,
    backgroundColor: colors.surface,
    padding: spacing.md,
    display: 'flex',
    flexDirection: 'column' as const,
    gap: spacing.sm,
  },
  reportingMetaRow: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))',
    gap: spacing.sm,
  },
  orderPanel: {
    border: `1px solid ${colors.border}`,
    borderRadius: borderRadius.md,
    backgroundColor: colors.surface,
    padding: spacing.md,
    display: 'flex',
    flexDirection: 'column' as const,
    gap: spacing.sm,
  },
  orderControlRow: {
    display: 'flex',
    flexWrap: 'wrap' as const,
    gap: spacing.sm,
    alignItems: 'flex-end',
  },
  orderControlLabel: {
    display: 'flex',
    flexDirection: 'column' as const,
    gap: spacing.xs,
    color: colors.textSecondary,
    fontSize: typographyFlat.fontSize.sm,
    minWidth: '120px',
  },
  orderControlInput: {
    padding: `${spacing.xs} ${spacing.sm}`,
    border: `1px solid ${colors.border}`,
    borderRadius: borderRadius.sm,
    fontSize: typographyFlat.fontSize.sm,
  },
  orderResultText: {
    color: colorExtensions.infoDark,
    fontSize: typographyFlat.fontSize.sm,
    fontWeight: typographyFlat.fontWeight.medium,
    whiteSpace: 'pre-wrap',
  },
  orderSummaryGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))',
    gap: spacing.sm,
  },
  logsTitle: {
    margin: 0,
    fontSize: typography.h3.fontSize,
    fontWeight: typography.h3.fontWeight,
    color: colors.text,
  },
};

export default ChannelDetailPage;
